import React from 'react'
import {useTranslation} from 'react-i18next'
import {RouteComponentProps} from 'react-router'
import {Redirect, Route, Switch} from 'react-router-dom'

import {Navigator} from '@progyny/components/Navigator'
import {ResponsiveContainer} from '@progyny/components/ResponsiveContainer'
import {
  SetupProfileMutationVariables,
  SetupMenopauseProfileMutationVariables,
  SetupPregnancyPostpartumProfileMutationVariables,
} from '@progyny/generated/graphql'
import useOnboardingMutations from '@progyny/hooks/useOnboardingMutations'
import {useProductLine} from '@progyny/hooks/useProductLine'
import {useProgressColor} from '@progyny/hooks/useProgressColor'
import CommonContainer from '@progyny/pages/CreateAccount/CommonContainer'
import useSetupProfileRoutes from '@progyny/pages/SetUpProfile/hooks/useSetupProfileRoutes'
import {SetupProfileRoutes} from '@progyny/pages/SetUpProfile/types'
import {useMeStore} from '@progyny/store/meStore'

const ChangeJourney: React.FC<RouteComponentProps> = (props) => {
  const {match, history} = props
  const {routes: subRoutes, navigateTo} = useSetupProfileRoutes()
  const {t} = useTranslation()
  const url = match.url.replace(/\/+$/, '')
  const onboardingData = useMeStore((s) => s.onboardingData)
  const {productLine} = useProductLine()
  const progressColor = useProgressColor()
  const {
    updateLoading,
    persistSetupInformation,
    persistMenopauseProfileInformation,
    persistPregnancyPostpartumProfileInformation,
  } = useOnboardingMutations()

  // Create a wrapper function that can handle all mutation types
  const handlePersistMemberData = (
    variables:
      | SetupProfileMutationVariables
      | SetupMenopauseProfileMutationVariables
      | SetupPregnancyPostpartumProfileMutationVariables,
    onSuccess: React.Dispatch<void>
  ) => {
    if (productLine === 'menopause') {
      persistMenopauseProfileInformation(
        variables as SetupMenopauseProfileMutationVariables,
        onSuccess
      )
    } else if (productLine === 'pregnancy_and_postpartum') {
      persistPregnancyPostpartumProfileInformation(
        variables as SetupPregnancyPostpartumProfileMutationVariables,
        onSuccess
      )
    } else {
      persistSetupInformation(
        variables as SetupProfileMutationVariables,
        onSuccess
      )
    }
  }

  return (
    <ResponsiveContainer>
      <Switch>
        {subRoutes.map((route, idx) => {
          const RouteComponent = route.component
          return (
            <Route
              key={idx}
              path={`${url}${route.path}`}
              render={() => (
                <CommonContainer
                  progressBar={true}
                  progressBarTitle={
                    route.showProgressBar === false
                      ? undefined
                      : productLine === 'menopause'
                        ? t('setup_profile_your_health_profile')
                        : t('setup_profile_your_family_plan')
                  }
                  headerTitle={route.headerTitle}
                  description={route.description}
                  progressValue={((idx + 1) / (subRoutes.length + 1)) * 100}
                  progressColor={progressColor}
                >
                  <RouteComponent
                    navigator={Navigator}
                    onboardingData={onboardingData}
                    callback={() => {
                      history.push(`${url}${subRoutes[idx + 1].path}`)
                    }}
                    updateLoading={updateLoading}
                    persistMemberData={handlePersistMemberData}
                    goBack={() => {
                      if (idx - 1 < SetupProfileRoutes.BIRTH_ASSIGNED_SEX) {
                        return
                      } else {
                        history.push(`${url}${subRoutes[idx - 1].path}`)
                      }
                    }}
                    match={match}
                    history={history}
                    navigateTo={navigateTo}
                  />
                </CommonContainer>
              )}
            />
          )
        })}
        <Route exact path={'/change-journey'}>
          <Redirect
            to={`${url}${
              subRoutes[SetupProfileRoutes.BIRTH_ASSIGNED_SEX].path
            }`}
          />
        </Route>
      </Switch>
    </ResponsiveContainer>
  )
}

export default ChangeJourney
